import React, { useRef, useCallback, useEffect } from "react";
import classNames from "classnames";
import { IForm, IWidget } from "@al/aku-lc-core";
import { connect, mapProps, useForm, useWidget } from "@al/aku-lc-renderer";

import { getPrefixCls } from "./helpers/prefixCls";
import { ValidateStatus } from "./types";
import { FormItemInput, IFormItemInputProps } from "./FormItemInput";
import styles from "./styles.scss";

type RenderChildren<Values = any> = (form: IForm<Values>) => React.ReactNode;

export interface IFormItemProps extends IFormItemInputProps {
  name?: string;
  className?: string;
  style?: React.CSSProperties;
  prefixCls?: string;
  children?: RenderChildren | React.ReactNode;
  hasFeedback?: boolean;
  validateStatus?: ValidateStatus;
  required?: boolean;
  hidden?: boolean;
  initialValue?: any;
  help?: React.ReactNode;
  errors?: Array<string>;
  touched?: boolean;
}

type ComposeFormItem = React.FC<IFormItemProps>;

interface MemoInputProps {
  value: any;
  update: number;
  children: React.ReactNode;
}

const MemoInput = React.memo(
  ({ children }: MemoInputProps) => children as JSX.Element,
  (prev, next) => prev.value === next.value && prev.update === next.update
);

MemoInput.displayName = "MemoInput";

const FormItemImpl: ComposeFormItem = (props) => {
  const widget = useWidget();
  const form = useForm();

  const { children, ...others } = props;
  const { prefixCls: customizePrefixCls, className, style, validateStatus, hidden, help, hasFeedback, errors } = others;
  const destroyRef = useRef(false);
  const prefixCls = getPrefixCls("form", customizePrefixCls);
  // const [domErrorVisible, innerSetDomErrorVisible] = React.useState(false);

  const setDomErrorVisible = (): void => {
    if (!destroyRef.current) {
      // innerSetDomErrorVisible(visible);
    }
  };

  const formProms = form?.path && widget?.query(form?.path)?.getProperty("componentProps");
  const isErrorToast = formProms?.errorTipsType === "Toast";

  useEffect(() => {
    if (isErrorToast && errors?.length) {
      // 优先显示一条错误信息
      widget?.env.notify.fail(errors[0]);
    }
  }, [errors, isErrorToast]);

  const getControlled = useCallback(() => {
    const namePath = props.name || widget?.path;
    let value;
    if (namePath) {
      value = form?.getValueIn(namePath);
    }

    return {
      value,
    };
  }, [form, widget?.path, props.name]);

  React.useEffect(
    () => (): void => {
      destroyRef.current = true;
    },
    []
  );

  const updateRef = useRef(0);
  updateRef.current += 1;

  const itemClassNames = classNames(className, {
    [styles[`${prefixCls}-item`]]: true,

    // status
    [styles[`${prefixCls}-item-has-feedback`]]: validateStatus && hasFeedback,
    // domErrorVisible 暂未使用，判断是否有错误可显示
    [styles[`${prefixCls}-item-with-help`]]:
      !isErrorToast && ((validateStatus === "error" && errors?.length) || !!help),
    [styles[`${prefixCls}-item-has-success`]]: validateStatus === "success",
    [styles[`${prefixCls}-item-has-warning`]]: validateStatus === "warning",
    [styles[`${prefixCls}-item-has-error`]]: validateStatus === "error",
    [styles[`${prefixCls}-item-is-validating`]]: validateStatus === "validating",
    [styles[`${prefixCls}-item-hidden`]]: hidden,
  });

  return (
    <div className={itemClassNames} style={style} key="row">
      <FormItemInput
        {...props}
        {...widget}
        errors={isErrorToast ? [] : (errors as Array<string>)}
        prefixCls={prefixCls}
        status={validateStatus}
        onErrorVisibleChange={setDomErrorVisible}
        validateStatus={validateStatus}
      >
        {React.isValidElement(children) ? React.cloneElement(children, { ...getControlled() }) : children}
      </FormItemInput>
    </div>
  );
};

export const FormItem: ComposeFormItem = connect(
  FormItemImpl,
  mapProps(
    (_: any, widget: IWidget<any>) => {
      return {
        errors: widget.errors,
      };
    },
    (_: any, widget: IWidget<any>) => {
      return {
        validateStatus: widget.validateStatus,
      };
    },
    (_: any, widget: IWidget<any>) => {
      return {
        required: widget.required,
        hidden: widget.hidden,
      };
    }
  )
);
